/*
    A campaign's Use Case (MPN-1689).

    Its own row under Sub-organization and Population segment, per the mockup, rather than a
    third column beside them -- the two above are about who the campaign reaches, and this is
    about what it is for.

    THE OPTIONS ARE FETCHED, NOT SHIPPED. They are table rows scoped to the campaign's
    vertical, and the ticket asks for new ones to arrive without a deploy. A list hardcoded
    here would make that false the moment somebody added one: it would exist, be settable
    through the API, appear on the holdout report, and be missing from the only dropdown
    anybody uses.
*/
import { useEffect, useState } from "react";

import {
    getUseCase, getUseCaseOptions, setUseCase, type UseCaseOption,
} from "@services/engagements/launches/useCaseService";

export default function UseCaseField({ launchId }: { launchId: number }) {
    const [options, setOptions] = useState<UseCaseOption[]>([]);
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let current = true;
        // Both, together. The options alone would render the field empty on a campaign that
        // already has a use case, which reads as "nobody has set this" -- and the next person
        // to set it would be overwriting a choice they were never shown.
        Promise.all([getUseCaseOptions(launchId), getUseCase(launchId)])
            .then(([rows, stored]) => {
                if (!current) return;
                setOptions(rows);
                setValue(stored.use_case || "");
                setLoaded(true);
            })
            .catch(() => { if (current) setLoaded(true); });
        return () => { current = false; };
    }, [launchId]);

    const choose = async (next: string) => {
        const previous = value;
        setValue(next);
        setSaving(true);
        setError(null);
        try {
            await setUseCase(launchId, next);
        } catch (e) {
            // Put it back. A select that silently keeps a value the server refused is worse
            // than one that flickers, because the page then disagrees with the report.
            setValue(previous);
            setError(e instanceof Error ? e.message : "Could not save the use case.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="campaign-card mt-3" style={{ maxWidth: 520 }}>
            <div className="campaign-label">Use case</div>
            <select
                className="form-select"
                aria-label="Use case"
                value={value}
                onChange={(event) => void choose(event.target.value)}
                disabled={saving || !loaded}
            >
                <option value="">Select a use case...</option>
                {options.map((option) => (
                    <option key={option.code} value={option.code}>{option.name}</option>
                ))}
            </select>
            {loaded && options.length === 0 ? (
                // Empty rather than another vertical's list. An empty dropdown is a visible
                // problem; borrowing a list would be an invisible one.
                <div className="form-text text-warning">
                    {/* The campaign is still usable; it just reports as Uncategorised until
                        this is sorted, and saying so is more use than "not set up yet". */}
                    No use cases exist for this campaign&apos;s vertical, so it will report as
                    Uncategorised on the holdout report. Ask engineering to check the vertical
                    is configured.
                </div>
            ) : (
                <div className="form-text">Options are scoped to this campaign's vertical.</div>
            )}
            {error && <div className="text-danger small mt-1" role="alert">{error}</div>}
        </div>
    );
}
